import { FormInstance } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import dayjs, { Dayjs } from 'dayjs';
import { flatten, isArray, isNumber, isObject, isString } from 'lodash';

import { Filter } from './filters.type';
import { SearchFormFieldEnum, SearchFormFieldsData, SearchFormInputEnum } from './searchFormField.types';
import {
  CriteriaOperatorEnum,
  CriteriaTypeEnum,
  SearchQueryCriteriaType,
  SearchQueryCriteriaValueType,
  SearchQueryRequestType,
} from './types';

const tableOrderEnum = {
  ascend: 'ASC',
  descend: 'DESC',
};

export const mapFilterToSearchQuery = (
  filter: Filter,
  pagingFrom: number,
  pageSize: number
): SearchQueryRequestType => {
  return {
    criteria: filter.criteria,
    sort: filter.sort,
    from: pagingFrom,
    max: pageSize,
  };
};

export const mapTableSorterToSearchQuerySorter = (
  sorter: SorterResult<Record<string, unknown>> | SorterResult<Record<string, unknown>>[]
): { field: string; order: string }[] => {
  return [sorter].flat().flatMap((element) => {
    if (!element.order || !element.field) return [];
    return [
      {
        field: element.field.toString(),
        order: tableOrderEnum[element.order],
      },
    ];
  });
};

export type SearchCriteriaType = Record<string, string | number | string[] | { start: Dayjs; end: Dayjs }>;

export const isSearchQueryCriteriaType = (
  criteria: SearchQueryCriteriaType | string[] | Dayjs
): criteria is SearchQueryCriteriaType => {
  const value = (criteria as SearchQueryCriteriaType)?.value;
  return value !== undefined;
};

const mapDateFieldToCriterion = (key: string, value: { start?: Dayjs; end?: Dayjs }) => {
  const dateCriteria = [];
  if (value['start']) {
    dateCriteria.push({
      field: key as SearchFormFieldEnum,
      value: dayjs(value['start'] as Dayjs).unix(),
      operator: CriteriaOperatorEnum.GE,
    });
  }
  if (value['end']) {
    dateCriteria.push({
      field: key as SearchFormFieldEnum,
      value: dayjs(value['end'] as Dayjs).unix(),
      operator: CriteriaOperatorEnum.LE,
    });
  }
  return dateCriteria;
};

// TODO: Add tests
export const mapFormValuesToPayload = (formValues: SearchCriteriaType): SearchQueryCriteriaType[] => {
  return Object.entries(formValues).flatMap(([key, value]): SearchQueryCriteriaType[] => {
    if (!value) return [];

    switch (getFormFieldData(key as SearchFormFieldEnum).searchFormFieldType) {
      case SearchFormInputEnum.formText:
        if (!isString(value)) return [];
        return [
          {
            field: key as SearchFormFieldEnum,
            value,
            operator:
              key === SearchFormFieldEnum.ExternalId ? CriteriaOperatorEnum.LK : CriteriaOperatorEnum.EQ,
          },
        ];

      case SearchFormInputEnum.formDatePicker:
        if (!isObject(value) || isArray(value)) return [];
        return mapDateFieldToCriterion(key, value);

      case SearchFormInputEnum.formSelect:
        if (!isString(value) && !isNumber(value)) return [];
        return [
          {
            field: key as SearchFormFieldEnum,
            value,
            operator: CriteriaOperatorEnum.EQ,
          },
        ];
      case SearchFormInputEnum.formCheckboxGroup:
      case SearchFormInputEnum.formMultipleSelect:
        if (!isArray(value) || value.length === 0) return [];
        const valuesToEnum = getFormFieldData(key as SearchFormFieldEnum).toPayloadValueFormatter?.(value) ?? [];
        return [
          {
            field: key as SearchFormFieldEnum,
            value: valuesToEnum,
            type: CriteriaTypeEnum.MUST,
            operator: CriteriaOperatorEnum.IN,
          },
        ];
      case SearchFormInputEnum.formCheckbox:
        if (typeof value !== 'boolean') return [];
        return [
          {
            field: key as SearchFormFieldEnum,
            value,
          },
        ];
      default:
        return [];
    }
  });
};

// TODO: Add tests
const mapDateCriteriaToFormValues = (criteria: SearchQueryCriteriaType, form: FormInstance, filter?: Filter) => {
  const criteriasForDate = filter?.criteria
    .filter((element) => element.field === criteria.field)
    .map((element) => element.value)
    .sort((a, b) => (a as number) - (b as number));

  const formatDate = (date: number) => dayjs.unix(date);

  const getStartDate = (dateCriterias: SearchQueryCriteriaValueType[]) => formatDate(dateCriterias[0] as number);
  const getEndDate = (dateCriterias: SearchQueryCriteriaValueType[]) => formatDate(dateCriterias[1] as number);

  // backward compatibility when only one date may be set but with all possible operators <, <=, >, >=
  if (criteriasForDate?.length === 1 && criteria.operator) {
    if ([CriteriaOperatorEnum.GE, CriteriaOperatorEnum.GT].includes(criteria.operator)) {
      form.setFieldValue([criteria.field, 'start'], formatDate(criteria.value as number));
    } else if ([CriteriaOperatorEnum.LE, CriteriaOperatorEnum.LT].includes(criteria.operator)) {
      form.setFieldValue([criteria.field, 'end'], formatDate(criteria.value as number));
    }
  }

  if (criteriasForDate?.length === 2) {
    form.setFieldValue([criteria.field, 'start'], getStartDate(criteriasForDate));
    form.setFieldValue([criteria.field, 'end'], getEndDate(criteriasForDate));
  }
};

// TODO: Add tests
export const setStaticFormValuesFromFilter = (form: FormInstance, filter?: Filter) => {
  function setFieldIfExists(criteria: SearchQueryCriteriaType) {
    if (!criteria.value || (isArray(criteria.value) && criteria.value.length === 0)) return;

    if (criteria.field)
      switch (getFormFieldData(criteria.field).searchFormFieldType) {
        case SearchFormInputEnum.formText:
          form.setFieldValue(criteria.field, criteria.value);
          break;
        case SearchFormInputEnum.formDatePicker:
          mapDateCriteriaToFormValues(criteria, form, filter);
          break;
        case SearchFormInputEnum.formMultipleSelect:
          const valuesForForm = getFormFieldData(criteria.field).toFormValueFormatter?.(
            flatten([criteria.value]) as string[]
          );
          form.setFieldValue(criteria.field, valuesForForm);
          break;
        case SearchFormInputEnum.formCheckbox:
          form.setFieldValue(criteria.field, criteria.value);
          break;
        case SearchFormInputEnum.formCheckboxGroup:
          const valuesForFormCheckboxGroup = getFormFieldData(criteria.field).toFormValueFormatter?.(
            flatten([criteria.value]) as string[]
          );
          form.setFieldValue(criteria.field, valuesForFormCheckboxGroup);
          break;
        case SearchFormInputEnum.formSelect:
          form.setFieldValue(criteria.field, criteria.value);
          break;
        case SearchFormInputEnum.formCheckbox:
          form.setFieldValue(criteria.field, criteria.value);
          break;
        default:
          return;
      }
  }

  form.resetFields();
  filter?.criteria.map((criteria) => {
    setFieldIfExists(criteria);
  });
};

export const getFormFieldData = (field: SearchFormFieldEnum) => SearchFormFieldsData[field];

export const isFormFieldOfDateType = (field: SearchFormFieldEnum) =>
  getFormFieldData(field).searchFormFieldType === SearchFormInputEnum.formDatePicker;

export const isFormFieldOfArrayType = (field: SearchFormFieldEnum) =>
  getFormFieldData(field).searchFormFieldType === SearchFormInputEnum.formMultipleSelect;

export const isFormFieldOfCandidateType = (field: SearchFormFieldEnum) => field === SearchFormFieldEnum.CandidateUUID;

export const isFormFieldOfProjectType = (field: SearchFormFieldEnum) => field === SearchFormFieldEnum.ProjectUUID;

export const isFormFieldOfBooleanType = (field: SearchFormFieldEnum) =>
  [SearchFormFieldEnum.ProjectActive, SearchFormFieldEnum.StatusActionRequired].includes(field);

export const isFormFieldOfUserType = (field: SearchFormFieldEnum) =>
  [
    SearchFormFieldEnum.ProjectBackOfficesId,
    SearchFormFieldEnum.ProjectCoordinatorsId,
    SearchFormFieldEnum.ProjectInterviewersId,
    SearchFormFieldEnum.ProjectInterviewersId,
    SearchFormFieldEnum.ProjectRecruitersId,
    SearchFormFieldEnum.ResponsibleId,
    SearchFormFieldEnum.Coordinators,
    SearchFormFieldEnum.Backoffices,
    SearchFormFieldEnum.Interviewers,
    SearchFormFieldEnum.Recruiters,
  ].includes(field);

export const isFormFieldOfProjectClientType = (field: SearchFormFieldEnum) =>
  field === SearchFormFieldEnum.ProjectClientId;
